import * as THREE from 'three';
import { collection, addDoc, doc, updateDoc, deleteDoc, getDoc, setDoc, FieldPath, documentId } from 'firebase/firestore';
import { useFirestore } from 'vuefire';
import { OrbitControlsEventMap, OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';

import loadingTextureUrl from '../assets/loading.png';

import * as ttUtils from './ttUtils';
import * as ttCmd from './ttCommands';
import { loadGlbAsset } from './ttGlbLoader';
import { Polyline } from './ttObject';
import { ttMetashapeCamera, ttMetashapeData, ttMetashapeParseUrl, ttMetashapeSensor } from './ttMetashapeParse';
import {
    fetchProjectConfig, imageUrl, imageUrlTif, metadataUrl, modelUrl,
    ProjectPointCloud,
    type NormalizedProjectConfig, type NormalizedProjectRegion, type ProjectTrajectory,
} from './ttProjectConfig';
import { lineMaterialModel, lineMaterialImage, markerMaterialImage } from './ttMaterials';

import { ViewHelper } from 'three/examples/jsm/helpers/ViewHelper.js';

import { markRaw, reactive, ref } from 'vue';
import { useCurrentUser, useFirebaseAuth } from 'vuefire';
import { loadPlyAsset } from './ttPlyLoader';
import { TIFFLoader } from 'three/examples/jsm/loaders/TIFFLoader.js';
import { modelViewPosition } from 'three/examples/jsm/nodes/Nodes.js';
import { ca } from 'vuetify/locale';


const trajectoryPointsCacheStep = 1.0;
export const cameraTrajectoryId = '__cameras__';
const mainCameraFarDistanceMultiplier = 5;
const fallbackModelTextureSize = 4096;
// 32 Mtexels occupy about 128 MiB at the base RGBA8 level, or 171 MiB with mipmaps.
const maxModelTexturePixels = 8192 * 4096;
type CachedTrajectoryCurve = THREE.Curve<THREE.Vector3> & {
    pointsCache?: THREE.Vector3[];
    pointsCacheStations?: number[];
};

function createPolylineCurve(points: THREE.Vector3[]): CachedTrajectoryCurve {
    const curve = new THREE.CurvePath<THREE.Vector3>();
    for (let index = 1; index < points.length; index++) {
        curve.add(new THREE.LineCurve3(points[index - 1].clone(), points[index].clone()));
    }
    return curve as CachedTrajectoryCurve;
}

function samplePolyline(points: THREE.Vector3[], maxStep: number): {
    samples: THREE.Vector3[];
    stations: number[];
} {
    if (!points.length) return { samples: [], stations: [] };
    const samples = [points[0].clone()];
    const stations = [0];
    let segmentStartStation = 0;
    for (let index = 1; index < points.length; index++) {
        const start = points[index - 1];
        const end = points[index];
        const segmentLength = start.distanceTo(end);
        const divisions = Math.max(1, Math.ceil(segmentLength / maxStep));
        for (let division = 1; division <= divisions; division++) {
            const ratio = division / divisions;
            samples.push(start.clone().lerp(end, ratio));
            stations.push(segmentStartStation + segmentLength * ratio);
        }
        segmentStartStation += segmentLength;
    }
    return { samples, stations };
}

function closestPointToTrajectory(trajectory: CachedTrajectoryCurve, point: THREE.Vector3, closestPointSta: THREE.Vector4 | null): THREE.Vector3 {
    const length = trajectory.getLength();
    if (length <= 0) {
        if (closestPointSta) closestPointSta.set(point.x, point.y, point.z, 0);
        return point.clone();
    }
    if (!trajectory.pointsCache || trajectory.pointsCache.length === 0) {
        trajectory.pointsCache = trajectory.getSpacedPoints(Math.max(1, Math.ceil(length / trajectoryPointsCacheStep)));
    }
    const closestPt = trajectory.pointsCache[0].clone();
    let minDist = point.distanceTo(closestPt);
    let minIndex = 0;
    for (let i = 1; i < trajectory.pointsCache.length; i++) {
        const currentDist = trajectory.pointsCache[i].distanceTo(point);
        if (currentDist < minDist) {
            minDist = currentDist;
            closestPt.copy(trajectory.pointsCache[i]);
            minIndex = i;
        }
    }
    if (closestPointSta) {
        const maxIndex = Math.max(1, trajectory.pointsCache.length - 1);
        const station = trajectory.pointsCacheStations?.[minIndex] ?? length * minIndex / maxIndex;
        closestPointSta.set(closestPt.x, closestPt.y, closestPt.z, station);
    }
    return closestPt;
}

function ttScene() {
    const object3D = THREE.Object3D as typeof THREE.Object3D & {
        DefaultUp?: THREE.Vector3;
    };
    const defaultUp = object3D.DEFAULT_UP ?? object3D.DefaultUp;
    defaultUp?.set(0, 1, 0);
    const scene = new THREE.Scene();
    const ambientLight = new THREE.AmbientLight(0xffffff, 1)
    ambientLight.name = "ambient_light";
    scene.add(ambientLight)
    return scene;
}

function addAxisAndLabel(scene: THREE.Scene, model: THREE.Object3D, axisSize: number, labelScale: number) {
    const pos = new THREE.Vector3();
    const qua = new THREE.Quaternion();
    model.getWorldPosition(pos);
    model.getWorldQuaternion(qua);
    const axes = new THREE.AxesHelper(axisSize);
    axes.position.copy(pos);
    axes.quaternion.copy(qua);
    scene.add(axes);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 256;
    canvas.width = size;
    canvas.height = size;
    ctx.fillText(`${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}`, size / 2, size / 2);  
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({map: texture, transparent: true, depthTest: false});
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(pos);
    sprite.scale.set(labelScale * size / 128, labelScale * size / 128, 1);
    scene.add(sprite);
}

function createMDIIconElement(viewer: Viewer, line: Polyline, iconName: string, x: number, y: number): HTMLElement {
    const icon = document.createElement('i');
    icon.className = `mdi ${iconName}`;
    icon.style.position = 'absolute';
    icon.style.fontSize = '24px';
    icon.style.color = `#${line.getColor().toString(16).padStart(6, '0')}`;
    icon.style.textShadow = `0px 0px 1px ${ttUtils.contrastColor(line.getColor())}`;
    icon.style.cursor = 'pointer';
    icon.style.left = `${x}px`;
    icon.style.top = `${y}px`;
    icon.style.width = '24px';
    icon.style.height = '24px';
    icon.style.lineHeight = '24px';
    icon.style.textAlign = 'center';
    icon.style.zIndex = '1';
    icon.style.pointerEvents = "auto";
    icon.style.transition = 'transform';
    icon.style.transformOrigin = '50% 100%';
    icon.custom_x = x;
    icon.custom_y = y;
    icon.custom_line = line;
    icon.style.transform = 'translate(-50%, -100%) scale(1)';

    icon.addEventListener('mouseenter', () => {
        icon.style.transform = 'translate(-50%, -100%) scale(1.25)';
    });

    icon.addEventListener('mouseleave', () => {
        icon.style.transform = 'translate(-50%, -100%) scale(1)';
    });

    icon.addEventListener('click', () => {
        if (viewer.selection.has(icon.custom_line)) {
            viewer.selection.remove(icon.custom_line);
        }
        else {
            viewer.selection.clear();
            viewer.selection.add(icon.custom_line);
        }
    });

    return icon;
}

function createSVGElement(viewer: Viewer, line: Polyline) {
    const element = document.getElementById("mainCanvasDivId");
    if (!element) {
        console.log("No mainCanvasDivId");
        return;
    }
    const width = element.clientWidth;
    const height = element.clientHeight;
    const v = new THREE.Vector3();
    line.vertexAt(0, v);
    v.project(viewer.camera);
    const x = (v.x * 0.5 + 0.5) * width;
    const y = (-v.y * 0.5 + 0.5) * height;
    const svg = createMDIIconElement(viewer, line, "mdi-map-marker", x, y);
    line.svg = svg;
    element.appendChild(svg);
    viewer.updateOnControlChange();
}

function createSVGElements(viewer: Viewer) {
    const element = document.getElementById("mainCanvasDivId");
    if (!element) {
        console.log("No mainCanvasDivId");
        return;
    }

    const width = element.clientWidth;
    const height = element.clientHeight;
    for (const line of viewer.lines) {
        if (!line.isMarker()) continue;
        const v = new THREE.Vector3();
        line.vertexAt(0, v);
        v.project(viewer.camera);
        const x = (v.x * 0.5 + 0.5) * width;
        const y = (-v.y * 0.5 + 0.5) * height;
        const svg = createMDIIconElement(viewer, line, "mdi-map-marker", x, y);
        line.svg = svg;
        element.appendChild(svg);
    }
    viewer.updateOnControlChange();
}

function cameraCenterFromMetashape(tM: THREE.Matrix4): THREE.Vector3 {
    // tM is the raw 4x4 from <camera><transform> (after transpose)
    const camToChunk = tM.clone().invert(); // camera->chunk(local, Z-up)
    return new THREE.Vector3().setFromMatrixPosition(camToChunk);
}

function cameraTrajectoryPoints(metashapeData: ttMetashapeData, minDist = 0.75, maxDist = 1.5): THREE.Vector3[] {
    const path: THREE.Vector3[] = [];
    // if (viewer.projectName === "logatec") {
    //     path.push(new THREE.Vector3(443240.0, 87360.0, 479.0));
    //     path.push(new THREE.Vector3(443250.0, 87400.0, 479.0));
    //     path.push(new THREE.Vector3(443260.0, 87440.0, 479.0));
    // }
    // else {
        const tfx = new THREE.Matrix4();
        const cameraVec = new THREE.Vector3();
        for (let msCamera of metashapeData.cameras) {
            let add = true;
            // const cameraLocal = cameraCenterFromMetashape(msCamera.transform).multiplyScalar(metashapeData.scale);
            // cameraVec.copy(cameraLocal);
            tfx.copy(metashapeData.transform);
            tfx.multiply(msCamera.transform);
            cameraVec.setFromMatrixPosition(tfx);
            if (path.length > 0 && path[path.length - 1].distanceTo(cameraVec) > maxDist) {
                add = false;
            }
            else {
                for (let i = 0; i < path.length; i++) {
                    if (path[i].distanceTo(cameraVec) < minDist) {
                        add = false;
                        break;
                    }
                }
            }
            if (add) {
                path.push(new THREE.Vector3(cameraVec.x, cameraVec.y, cameraVec.z));
            }
        }
        path.reverse();

    return path;
}

function ttCreateCamerasAndSetViewport(viewer: Viewer, metashapeData: ttMetashapeData, initializeView: boolean) {
    // const firstCam = metashapeData.cameras[0];
    // const firstCenterLocal = cameraCenterFromMetashape(firstCam.transform).multiplyScalar(metashapeData.scale);
    // const firstPosThree = new THREE.Vector3(firstCenterLocal.x, firstCenterLocal.z, -firstCenterLocal.y);

    const tfx = new THREE.Matrix4();
    tfx.copy(metashapeData.transform);
    tfx.multiply(metashapeData.cameras[0].transform);
    if (initializeView && viewer.controls) {
        viewer.camera.position.setFromMatrixPosition(tfx);
        const lookAtVec = new THREE.Vector3(0, 0, 1);
        lookAtVec.applyMatrix4(tfx);
        viewer.controls.target.copy(lookAtVec);
    }
    for (let msCamera of metashapeData.cameras) {

        const cameraAxes = new THREE.AxesHelper(0.1);
        tfx.copy(metashapeData.transform);
        tfx.multiply(msCamera.transform);
        var cameraZaxis = new THREE.Vector3(0, 0, 1).applyMatrix4(tfx)
        var cameraXaxis = new THREE.Vector3(1, 0, 0).applyMatrix4(tfx)
        var cameraYaxis = new THREE.Vector3(0, 1, 0).applyMatrix4(tfx)
        switch (msCamera.orientation) {
        case 2: cameraXaxis.negate(); break;
        case 3: cameraXaxis.negate(); cameraYaxis.negate(); break;
        case 4: cameraYaxis.negate(); break;
        case 5: cameraZaxis = cameraXaxis; cameraXaxis = cameraYaxis; cameraYaxis = cameraZaxis; break;
        case 6: cameraZaxis = cameraXaxis; cameraXaxis = cameraYaxis.negate(); cameraYaxis = cameraZaxis; break;
        case 7: cameraZaxis = cameraXaxis.negate(); cameraXaxis = cameraYaxis.negate(); cameraYaxis = cameraZaxis; break;
        case 8: cameraZaxis = cameraXaxis.negate(); cameraXaxis = cameraYaxis; cameraYaxis = cameraZaxis; break;
        }
        if(msCamera.orientation > 1)
            tfx.makeBasis(cameraXaxis, cameraYaxis, cameraZaxis);

        cameraAxes.up.set(0, 1, 0);
        cameraAxes.applyMatrix4(tfx);

        const sensor = metashapeData.sensors.find(e => e.id === msCamera.sensorId);
        if(sensor){
            var camera = new THREE.PerspectiveCamera(20, sensor.widthPx / sensor.heightPx, -0.05, -0.2);
            const cameraFrustum = new THREE.CameraHelper(camera);
            cameraFrustum.applyMatrix4(tfx);
            viewer.msCameraFrustums.push(cameraFrustum);
            viewer.scene.add(cameraFrustum);
            cameraFrustum.visible = viewer.settings.get('cameras_visible') === true;
        }

        // console.log(cameraGeom.position);
        viewer.msCameras.push(cameraAxes);
        cameraAxes.userData.msSensorId = msCamera.sensorId;
        cameraAxes.userData.msLabel = msCamera.label;
        cameraAxes.userData.msComponentId = msCamera.componentId;
        viewer.scene.add(cameraAxes);
        cameraAxes.visible = viewer.settings.get('cameras_visible') === true;
    }
    if (!viewer.hasCustomTrajectories) {
        viewer.setTrajectoryPoints(cameraTrajectoryId, cameraTrajectoryPoints(metashapeData));
    }
}

function projectLinesOnImage(viewer: Viewer, cameraIdx: number) {
    if (!viewer.imageCanvas || !viewer.metashapeData
        || cameraIdx < 0 || cameraIdx >= viewer.metashapeData.cameras.length)
        return;
    for (let drawnImageLine of viewer.drawnLinesImage) {
        viewer.imageScene.remove(drawnImageLine);
        drawnImageLine.geometry.dispose();
    }
    viewer.drawnLinesImage = [];
    const camera = viewer.metashapeData.cameras[cameraIdx];
    const sensor = viewer.metashapeData.sensors.find(e => e.id === camera.sensorId);
    if (!sensor)
        return;
    const inProgressLine = viewer.currentCommand.value instanceof ttCmd.CreatePolylineCmd
        ? viewer.currentCommand.value.line
        : null;
    const markerPoint = new THREE.Vector3();
    const markerImagePoint = new THREE.Vector2();
    const markerPlanePoint = new THREE.Vector2();
    for (const line of viewer.lines) {
        if (!line.obj.visible) continue;
        if (line === inProgressLine) continue;
        if (line.isMarker()) {
            line.vertexAt(0, markerPoint);
            markerPoint.applyMatrix4(camera.worldToCamera);
            const projected = ttUtils.projectCameraPointToImagePixel(markerPoint, sensor, markerImagePoint);
            if (!projected) continue;
            ttUtils.imagePixelToPlanePoint(
                sensor,
                projected.x,
                projected.y,
                camera.orientation,
                markerPlanePoint,
            );
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute([
                markerPlanePoint.x,
                markerPlanePoint.y,
                0.2,
            ], 3));
            const marker = new THREE.Points(geometry, markerMaterialImage[line.severity]);
            marker.renderOrder = 2;
            viewer.imageScene.add(marker);
            viewer.drawnLinesImage.push(marker);
            continue;
        }
        const geometry = ttUtils.projectLineOnImage(
            line,
            camera.worldToCamera,
            sensor,
            camera.orientation,
        );
        if (geometry) {
            const newLine = new LineSegments2(geometry, lineMaterialImage[line.severity]);
            newLine.computeLineDistances();
            newLine.renderOrder = 1;
            viewer.imageScene.add(newLine);
            viewer.drawnLinesImage.push(newLine);
        }
    }
    viewer.currentCommand.value?.onImageCanvasChanged(viewer.imageCanvas, viewer.orthoControls);
}

function regionIdForObject(object: THREE.Object3D | null): string | null {
    let current = object;
    while (current) {
        const regionId = current.userData?.ttRegionId;
        if (typeof regionId === 'string') return regionId;
        current = current.parent;
    }
    return null;
}

async function activateIntersectedRegion(viewer: Viewer, intersection: THREE.Intersection): Promise<void> {
    const regionId = regionIdForObject(intersection.object);
    if (!regionId || regionId === viewer.activeRegionId.value) return;
    await viewer.setActiveRegion(regionId);
}

function mainCanvasClickHandler(viewer: Viewer, event: MouseEvent) {
    if (event.button !== 0)
        return;
    if (viewer.currentCommand.value) //TODO: remove listener
        return;

    const modelIntersection = ttUtils.raycastPointOnMainModel(viewer, event);
    if (modelIntersection) void activateIntersectedRegion(viewer, modelIntersection);

    if (!viewer.canvas) return;
    const mouse = ttUtils.toNormalizedScreenCoord(event.clientX, event.clientY, viewer.canvas);
    viewer.raycaster.setFromCamera(mouse, viewer.camera);
    const intersects = viewer.raycaster.intersectObjects(viewer.scene.children, true);
    for (const intersection of intersects) {
        if (!intersection.object.visible) continue;
        for (const line of viewer.lines) {
            if (line.obj == intersection.object) {
                if (viewer.selection.has(line)) {
                    viewer.selection.remove(line);
                }
                else {
                    viewer.selection.add(line);
                }
                break;
            }
        }
    }
}

function findClosestCamera(
    viewer: Viewer,
    data: ttMetashapeData,
    point: THREE.Vector3,
    preferredViewRay?: THREE.Vector3,
)
{
    let bestScore = Number.POSITIVE_INFINITY;
    let minIdx = -1;
    const cameraPoint = new THREE.Vector3();
    const imagePoint = new THREE.Vector2();
    const cameraPosition = new THREE.Vector3();
    const candidateRay = new THREE.Vector3();
    const viewerRay = preferredViewRay?.clone().normalize()
        ?? new THREE.Vector3().subVectors(point, viewer.camera.position).normalize();
    const cameraToWorld = new THREE.Matrix4();
    for (let i = 0; i < data.cameras.length; i++) {
        const camera = data.cameras[i];
        const sensor = data.sensors.find((item) => item.id === camera.sensorId);
        if (!sensor) continue;
        cameraPoint.copy(point).applyMatrix4(camera.worldToCamera);
        const projected = ttUtils.projectCameraPointToImagePixel(cameraPoint, sensor, imagePoint);
        if (!projected || projected.x < 0 || projected.x > sensor.widthPx
            || projected.y < 0 || projected.y > sensor.heightPx) continue;

        cameraToWorld.copy(camera.worldToCamera).invert();
        cameraPosition.setFromMatrixPosition(cameraToWorld);
        candidateRay.subVectors(point, cameraPosition);
        const distance = candidateRay.length();
        if (distance <= Number.EPSILON) continue;
        candidateRay.multiplyScalar(1 / distance);

        // Prefer a photo taken from the same side/direction as the user's 3D
        // view, then a point near the photo centre, then the nearer camera.
        const directionPenalty = 1 - THREE.MathUtils.clamp(candidateRay.dot(viewerRay), -1, 1);
        const centerX = (projected.x - sensor.widthPx * 0.5) / (sensor.widthPx * 0.5);
        const centerY = (projected.y - sensor.heightPx * 0.5) / (sensor.heightPx * 0.5);
        const centerPenalty = Math.hypot(centerX, centerY);
        const score = directionPenalty * 10 + centerPenalty + distance * 0.001;
        if (score < bestScore) {
            bestScore = score;
            minIdx = i;
        }
    }
    return minIdx;
}

async function mainCanvasDblClickHandler(viewer: Viewer, event: MouseEvent) {
    if (event.button === 1) {
        viewer.zoomToSceneExtents();
        return;
    }
    if (viewer.currentCommand.value) //TODO: remove listener
        return;
    if (!viewer.controls)
        return;
    const intersObj = ttUtils.raycastPointOnMainModel(viewer, event);
    if (intersObj === null)
        return;
    await activateIntersectedRegion(viewer, intersObj);
    if (!viewer.metashapeData)
        return;
    const intersection = intersObj.point;
    const clickedViewRay = new THREE.Vector3().subVectors(intersection, viewer.camera.position).normalize();
    const viewDir = new THREE.Vector3(0, 0, 1);
    const intersToPos = new THREE.Vector3();
    viewDir.subVectors(viewer.camera.position, viewer.controls.target).normalize();
    intersToPos.subVectors(intersection, viewer.camera.position);
    const projLen = intersToPos.dot(viewDir);
    viewer.controls.target.copy(intersection);
    const newCameraPos = new THREE.Vector3(projLen * viewDir.x, projLen * viewDir.y, projLen * viewDir.z);
    newCameraPos.subVectors(intersection, newCameraPos);
    viewer.camera.position.copy(newCameraPos);
    let minIdx = findClosestCamera(viewer, viewer.metashapeData, intersection, clickedViewRay);
    if (minIdx < 0 || minIdx >= viewer.metashapeData.cameras.length) return;
    viewer.cameraIndex = minIdx;
    viewer.imageLabel.value = viewer.metashapeData.cameras[minIdx].label;
    
    // Show camera position and orientation for the selected camera
    const camera = viewer.metashapeData.cameras[minIdx];
    var tfx = new THREE.Matrix4();
    tfx.copy(viewer.metashapeData.transform);
    tfx.multiply(camera.transform);

    var cameraZaxis = new THREE.Vector3();
    var cameraXaxis = new THREE.Vector3();
    var cameraYaxis = new THREE.Vector3();
    tfx.extractBasis(cameraXaxis, cameraYaxis, cameraZaxis);
    switch (camera.orientation) {
    case 2: cameraXaxis.negate(); break;
    case 3: cameraXaxis.negate(); cameraYaxis.negate(); break;
    case 4: cameraYaxis.negate(); break;
    case 5: cameraZaxis = cameraXaxis; cameraXaxis = cameraYaxis; cameraYaxis = cameraZaxis; break;
    case 6: cameraZaxis = cameraXaxis; cameraXaxis = cameraYaxis.negate(); cameraYaxis = cameraZaxis; break;
    case 7: cameraZaxis = cameraXaxis.negate(); cameraXaxis = cameraYaxis.negate(); cameraYaxis = cameraZaxis; break;
    case 8: cameraZaxis = cameraXaxis.negate(); cameraXaxis = cameraYaxis; cameraYaxis = cameraZaxis; break;
    }
    if(camera.orientation > 1)
        tfx.makeBasis(cameraXaxis, cameraYaxis, cameraZaxis);

    const cameraAxes = new THREE.AxesHelper(0.1);
    cameraAxes.up.set(0, 1, 0);
    cameraAxes.applyMatrix4(tfx);

    const sensor = viewer.metashapeData.sensors.find(e => e.id === camera.sensorId);
    if(sensor){
        var cameraHelper = new THREE.PerspectiveCamera(20, sensor.widthPx / sensor.heightPx, -0.2, -0.6);
        const cameraFrustum = new THREE.CameraHelper(cameraHelper);
        cameraFrustum.applyMatrix4(tfx);
        if(viewer.msCameraFrustum) viewer.scene.remove(viewer.msCameraFrustum);
        viewer.msCameraFrustum = cameraFrustum;
        viewer.scene.add(cameraFrustum);
        
    }
    if(viewer.msCamera) viewer.scene.remove(viewer.msCamera);
    viewer.msCamera = cameraAxes;
    viewer.scene.add(cameraAxes);
    
    var viewYaxis = new THREE.Vector3(0, 1, 0)
    viewer.camera.matrixWorld.extractBasis(new THREE.Vector3(), viewYaxis, new THREE.Vector3());
    viewer.flipImage.value = viewYaxis.dot(cameraYaxis) > 0;
    
    const url = viewer.activeImageUrl(viewer.imageLabel.value);
    if (!url) return;
    const urlTif = viewer.activeImageUrlTif(viewer.imageLabel.value);
    if(!urlTif) return;
    const activeId = viewer.activeRegionId.value;
    console.log(`Loading region ${viewer.activeRegionId.value} image: ${viewer.imageLabel.value}`);

    if (!viewer.imageMaterial) viewer.imageMaterial = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
    viewer.imageMaterial.map?.dispose();
    viewer.imageMaterial.map = new THREE.TextureLoader().load(loadingTextureUrl);
    viewer.imageMaterial.needsUpdate = true;
    projectLinesOnImage(viewer, minIdx);

    const loaderTif = new TIFFLoader();
    const loader = new THREE.TextureLoader();
    try {
        const textureTif = await loaderTif.loadAsync(urlTif)
        if (viewer.disposed || viewer.activeRegionId.value !== activeId) {
            textureTif.dispose();
        }
        if (!viewer.imageMaterial) viewer.imageMaterial = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
        viewer.imageMaterial.map?.dispose();
        viewer.imageMaterial.map = textureTif;
        viewer.imageMaterial.needsUpdate = true;
        viewer.resetImageViewport(viewer.flipImage.value);
        viewer.setRegionError(activeId, '', '', false);
    } catch (error) {
        //console.error(`Failed to load camera image ${urlTif}: ${error instanceof Error ? error.message : String(error)}`);
        try {
            const texture = await loader.loadAsync(url);
            if (viewer.disposed || viewer.activeRegionId.value !== activeId) {
                texture.dispose();
                return;
            }
            if (!viewer.imageMaterial) viewer.imageMaterial = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
            
            viewer.imageMaterial.map?.dispose();
            viewer.imageMaterial.map = texture;
            viewer.imageMaterial.needsUpdate = true;
            viewer.resetImageViewport(viewer.flipImage.value);
            viewer.setRegionError(activeId, '', '', false);
            return;
        } catch (error) {
            viewer.setRegionError(activeId, `Failed to load camera image ${url}: ${error instanceof Error ? error.message : String(error)}`, url, false);
            return;
        }
        
    }
    
}

function imageCanvasDblClickHandler(viewer: Viewer, event: MouseEvent) {
}

class UndoStack {
    undo: ttCmd.CmdBase[] = reactive([]);
    redo: ttCmd.CmdBase[] = reactive([]);
    max:  number    = 50;
}

export class Selection {
    viewer: Viewer;
    objects: Polyline[] = reactive([]);

    constructor(v: Viewer) {
        this.viewer = v;
    }

    add(line: Polyline) {
        if (this.has(line))
            return;
        this.objects.push(line);
        line.setMaterial(lineMaterialModel.length - 1);
    }

    addRange(start: number, end: number) {
        const s = Math.min(start, end);
        if (s < 0)
            return;
        const e = Math.min(Math.max(start, end), this.viewer.lines.length - 1);
        for (let i = s; i <= e; i++) {
            this.add(this.viewer.lines[i]);
        }
    }

    remove(line: Polyline) {
        const index = this.objects.indexOf(line);
        if (index > -1) {
            this.objects.splice(index, 1);
            line.setMaterial(line.severity);
        }
    }

    clear() {
        for (const line of this.objects) {
            line.setMaterial(line.severity);
        }
        this.objects.length = 0;
    }

    has(line: Polyline): boolean {
        return this.objects.includes(line);
    }

    clone(): Selection {
        const res = new Selection(this.viewer);
        for (const o of this.objects)
            res.add(o);
        return res;
    }
}

export class Settings {
    name: string = 'ttSettings';
    data: object = {
        'cameras_visible': false,
        'trajectory_visible': true,
    };

    constructor() {
        if (window.localStorage[name] === undefined) {
            window.localStorage[name] = JSON.stringify(this.data);
        } else {
            const data = JSON.parse(window.localStorage[name]);
            for (const key in data) {
                this.data[key] = data[key];
            }
        }
    }

    get(key: string) {
        return this.data[key];
    }

    set(key: string, val: any) {
        this.data[key] = val;
        window.localStorage[this.name] = JSON.stringify(this.data);
    }
}

export type RegionLoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface RegionRuntime {
    status: RegionLoadStatus;
    error: string | null;
    errorUrl: string | null;
    models: THREE.Object3D[];
    metashapeData: ttMetashapeData | null;
    generation: number;
    abortController: AbortController | null;
    loadPromise: Promise<void> | null;
    bounds: THREE.Box3 | null;
}

export interface ProjectPointCloudRuntime {
    pointCloud: THREE.Points | null;
    status: RegionLoadStatus;
    error: string | null;
    errorUrl: string | null;
    abortController: AbortController | null;
    loadPromise: Promise<void> | null;
    bounds: THREE.Box3 | null;
}

interface RegionImageContext {
    regionId: string;
    data: ttMetashapeData;
    camera: ttMetashapeCamera;
    sensor: ttMetashapeSensor;
    models: THREE.Object3D[];
}

export interface CursorWorldPosition {
    x: number;
    y: number;
    z: number;
    valid: boolean;
    source: 'model' | 'ground';
}

interface SavedRegionState {
    activeRegionId: string | null;
    selectedRegionIds: string[];
}

interface TrajectoryStationSample {
    internalStation: number;
    horizontalStation: number;
}

function disposeTexture(texture: THREE.Texture, disposedTextures: Set<THREE.Texture>) {
    if (disposedTextures.has(texture)) return;
    disposedTextures.add(texture);
    const image = texture.source?.data;
    texture.dispose();
    if (typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
        image.close();
        texture.source.data = null;
    }
}

function disposeMaterial(
    material: THREE.Material,
    disposedMaterials: Set<THREE.Material>,
    disposedTextures: Set<THREE.Texture>,
) {
    if (disposedMaterials.has(material)) return;
    disposedMaterials.add(material);
    const values = material as unknown as Record<string, unknown>;
    for (const value of Object.values(values)) {
        if (value instanceof THREE.Texture) disposeTexture(value, disposedTextures);
    }
    material.dispose();
}

function disposeObject(object: THREE.Object3D) {
    const disposedMaterials = new Set<THREE.Material>();
    const disposedTextures = new Set<THREE.Texture>();
    object.traverse((child) => {
        const renderable = child as THREE.Mesh;
        renderable.geometry?.dispose?.();
        const material = renderable.material;
        if (Array.isArray(material)) {
            material.forEach((item) => disposeMaterial(item, disposedMaterials, disposedTextures));
        } else if (material) {
            disposeMaterial(material, disposedMaterials, disposedTextures);
        }
    });
}

export class Viewer {
    renderer:          THREE.WebGLRenderer | null = null;
    imageRenderer:     THREE.WebGLRenderer | null = null;
    canvas:            HTMLElement | null = null;
    imageCanvas:       HTMLElement | null = null;
    imageMesh:         THREE.Mesh = new THREE.Mesh();
    msCameras:         THREE.AxesHelper[] = [];
    msCameraFrustums:  THREE.CameraHelper[] = [];
    msCamera:         THREE.AxesHelper | null = null;
    msCameraFrustum:  THREE.CameraHelper | null = null;
    camera:            THREE.PerspectiveCamera = new THREE.PerspectiveCamera(
        50,
        1,
        0.1,
        2000 * mainCameraFarDistanceMultiplier,
    );
    orthoCamera:       THREE.OrthographicCamera = new THREE.OrthographicCamera();
    controls?:         OrbitControls;
    orthoControls?:    OrbitControls;
    scene:             THREE.Scene = ttScene();
    imageScene:        THREE.Scene = ttScene();
    metashapeData:     ttMetashapeData | null = null;
    cameraIndex:       number = -1;
    raycaster:         THREE.Raycaster = new THREE.Raycaster;
    models:            THREE.Object3D[] = [];
    trajectory:        CachedTrajectoryCurve = createPolylineCurve([]);
    trajectoryPos      = ref<THREE.Vector4>(new THREE.Vector4());
    trajectoryLength   = ref(0);
    trajectoryHorizontalPos = ref(0);
    trajectoryHorizontalLength = ref(0);
    trajectoryStartStation = ref(0);
    trajectoryStationIncreasing = ref(true);
    flipImage = ref(false);
    activeTrajectoryId = ref<string | null>(null);
    trajectoryStateVersion = ref(0);
    projectConfig:     NormalizedProjectConfig | null = null;
    availableRegions:  NormalizedProjectRegion[] = reactive([]);
    selectedRegionIds  = ref<string[]>([]);
    activeRegionId     = ref<string | null>(null);
    regionStateVersion = ref(0);
    regionRuntimeById: Map<string, RegionRuntime> = markRaw(new Map());
    disposed = false;
    cursorWorldPosition: CursorWorldPosition = reactive({ x: 0, y: 0, z: 0, valid: false, source: 'ground' });
    private box3Helper = new THREE.Box3();
    private boxSize    = new THREE.Vector3();
    private boxCenter  = new THREE.Vector3();

    currentCommand     = ref<ttCmd.CmdBase | null>(null);
    onDistanceCommandStarted?: () => void;

    lines:             Polyline[] = reactive([]);
    drawnLinesImage:   Array<LineSegments2 | THREE.Points> = []; //TODO: combine with Polyline?

    projectPointClouds:    ProjectPointCloud[] = reactive([]);
    selectedPointCloudIds  = ref<string[]>([]);
    projectPointCloudRuntimeById: Map<string, ProjectPointCloudRuntime> = markRaw(new Map());
    pointCloudSize:          number = 0.04;
    textureCompression:      boolean = true;

    imageLabel         = ref<string>("Default.JPG");
    imageMaterial:     THREE.MeshLambertMaterial | null = null;

    currentSeverity:   number = 1;

    viewHelper:        ViewHelper;
    undoStack:         UndoStack = new UndoStack();
    settings:          Settings = new Settings();
    selection:         Selection = new Selection(this);

    projectName:       string = '';
    debug:             boolean = false;
    

    //TODO: remove
    // stats: Stats = new Stats();
    private mainCanvasClickFn?: (e: MouseEvent) => void;
    private mainCanvasDblClickFn?: (e: MouseEvent) => void;
    private mainCanvasAuxClickFn?: (e: MouseEvent) => void;
    private imageCanvasDblClickFn?: (e: MouseEvent) => void;
    private imageCanvasContextMenuFn?: (e: Event) => void;
    private imageCanvasAuxClickFn?: (e: MouseEvent) => void;
    private keydownFn?: (e: KeyboardEvent) => void;
    private mainCanvasPointerMoveFn?: (e: PointerEvent) => void;
    private mainCanvasPointerLeaveFn?: () => void;
    private cursorFrameId: number | null = null;
    private cursorClientX = 0;
    private cursorClientY = 0;
    private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    private readonly cursorPoint = new THREE.Vector3();
    private readonly imageViewportSensorSize = new THREE.Vector2();
    private readonly maxConcurrentRegionLoads = 2;
    private activeRegionLoads = 0;
    private regionLoadWaiters: Array<() => void> = [];
    private initializedView = false;
    private trajectoryStationSamples: TrajectoryStationSample[] = [];

    constructor(user: User, projectName: string) {
        this.projectName = projectName;
    }

    get customTrajectories(): ProjectTrajectory[] {
        return this.projectConfig?.trajectories ?? [];
    }

    get hasCustomTrajectories(): boolean {
        return this.customTrajectories.length > 0;
    }

    setActiveTrajectory(trajectoryId: string): boolean {
        const trajectory = this.customTrajectories.find((candidate) => candidate.id === trajectoryId);
        if (!trajectory) return false;
        this.setTrajectoryPoints(
            trajectory.id,
            trajectory.points.map((point) => new THREE.Vector3(point.x, point.y, point.z)),
            trajectory.startStation ?? 0,
            trajectory.stationIncreasing !== false,
        );
        return true;
    }

    setTrajectoryPoints(
        trajectoryId: string,
        points: THREE.Vector3[],
        startStation = 0,
        stationIncreasing = true,
    ) {
        this.clearTrajectory();
        const path = points.filter((point) => [point.x, point.y, point.z].every(Number.isFinite));
        this.activeTrajectoryId.value = trajectoryId;
        this.trajectoryStartStation.value = Number.isFinite(startStation) ? startStation : 0;
        this.trajectoryStationIncreasing.value = stationIncreasing;
        this.trajectory = createPolylineCurve(path);
        this.trajectory.pointsCache = [];
        const length = path.length > 1 ? this.trajectory.getLength() : 0;
        this.trajectoryLength.value = length;
        if (path.length < 2 || length <= 0) {
            this.trajectoryPos.value.set(0, 0, 0, 0);
            this.trajectoryStateVersion.value++;
            return;
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(path);
        const material = new THREE.LineBasicMaterial({ color: 0xff0000 });
        const trajectoryLine = new THREE.Line(geometry, material);
        trajectoryLine.name = 'trajectory';
        trajectoryLine.visible = this.settings.get('trajectory_visible') !== false;
        const { samples, stations } = samplePolyline(path, trajectoryPointsCacheStep);
        this.trajectory.pointsCache = samples;
        this.trajectory.pointsCacheStations = stations;
        let horizontalStation = 0;
        let internalStation = 0;
        this.trajectoryStationSamples = path.map((point, index) => {
            if (index > 0) {
                const previous = path[index - 1];
                internalStation += point.distanceTo(previous);
                horizontalStation += Math.hypot(point.x - previous.x, point.y - previous.y);
            }
            return { internalStation, horizontalStation };
        });
        this.trajectoryHorizontalLength.value = horizontalStation;
        this.scene.add(trajectoryLine);
        closestPointToTrajectory(this.trajectory, this.camera.position, this.trajectoryPos.value);
        this.syncHorizontalStation();
        this.trajectoryStateVersion.value++;
    }

    private syncHorizontalStation() {
        this.trajectoryHorizontalPos.value = this.horizontalStationAtInternal(this.trajectoryPos.value.w);
    }

    private horizontalStationAtInternal(internalStation: number): number {
        const samples = this.trajectoryStationSamples;
        if (!samples.length) return 0;
        const value = THREE.MathUtils.clamp(internalStation, 0, this.trajectoryLength.value);
        let upperIndex = samples.findIndex((sample) => sample.internalStation >= value);
        if (upperIndex < 0) return samples[samples.length - 1].horizontalStation;
        if (upperIndex === 0) return samples[0].horizontalStation;
        const lower = samples[upperIndex - 1];
        const upper = samples[upperIndex];
        const span = upper.internalStation - lower.internalStation;
        const ratio = span > 0 ? (value - lower.internalStation) / span : 0;
        return THREE.MathUtils.lerp(lower.horizontalStation, upper.horizontalStation, ratio);
    }

    private internalStationAtHorizontal(horizontalStation: number): number {
        const samples = this.trajectoryStationSamples;
        if (!samples.length) return 0;
        const value = THREE.MathUtils.clamp(horizontalStation, 0, this.trajectoryHorizontalLength.value);
        let upperIndex = samples.findIndex((sample) => sample.horizontalStation >= value);
        if (upperIndex < 0) return samples[samples.length - 1].internalStation;
        if (upperIndex === 0) return samples[0].internalStation;
        const lower = samples[upperIndex - 1];
        const upper = samples[upperIndex];
        const span = upper.horizontalStation - lower.horizontalStation;
        const ratio = span > 0 ? (value - lower.horizontalStation) / span : 1;
        return THREE.MathUtils.lerp(lower.internalStation, upper.internalStation, ratio);
    }

    setHorizontalStation(value: number) {
        if (this.trajectoryHorizontalLength.value <= 0) return;
        this.setStation(this.internalStationAtHorizontal(value));
    }

    private clearTrajectory() {
        const trajectoryLine = this.scene.getObjectByName('trajectory') as THREE.Line | undefined;
        if (trajectoryLine) {
            this.scene.remove(trajectoryLine);
            trajectoryLine.geometry.dispose();
            const material = trajectoryLine.material;
            if (Array.isArray(material)) material.forEach((item) => item.dispose());
            else material.dispose();
        }
        this.trajectory = createPolylineCurve([]);
        this.trajectory.pointsCache = [];
        this.trajectoryLength.value = 0;
        this.trajectoryHorizontalPos.value = 0;
        this.trajectoryHorizontalLength.value = 0;
        this.trajectoryStartStation.value = 0;
        this.trajectoryStationIncreasing.value = true;
        this.trajectoryStationSamples = [];
        this.trajectoryPos.value.set(0, 0, 0, 0);
        this.activeTrajectoryId.value = null;
    }

    async afterMount() {
        if (this.projectConfig || this.disposed) return;
        this.projectConfig = await fetchProjectConfig(this.projectName);

        this.projectPointClouds = this.projectConfig.projectPointClouds ?? [];
        for(const pointCloud of this.projectPointClouds) {
            this.projectPointCloudRuntimeById.set(pointCloud.id, {
                pointCloud: null,
                status: 'idle',
                error: null,
                errorUrl: null,
                abortController: null,
                loadPromise: null,
                bounds: null,
            });
        }

        this.availableRegions.splice(0, this.availableRegions.length, ...this.projectConfig.regions);
        for (const region of this.availableRegions) this.regionRuntimeById.set(region.id, this.newRegionRuntime());
        const savedState = await this.loadSavedRegionState();
        const allowedRegionIds = new Set(this.availableRegions.filter((region) => region.enabled).map((region) => region.id));
        const selectedRegionIds = savedState.selectedRegionIds.filter((regionId) => allowedRegionIds.has(regionId));
        const savedRegion = savedState.activeRegionId
            ? this.availableRegions.find((region) => region.id === savedState.activeRegionId && region.enabled && region.canActivate)
            : null;
        const initial = savedRegion?.id ?? this.projectConfig.initialRegionId;
        if (initial && !selectedRegionIds.includes(initial)) selectedRegionIds.push(initial);
        await Promise.all([
            selectedRegionIds.length ? this.selectRegions(selectedRegionIds, initial) : Promise.resolve(),
            this.loadServer(),
        ]);
        if (this.hasCustomTrajectories && this.projectConfig.initialTrajectoryId) {
            this.setActiveTrajectory(this.projectConfig.initialTrajectoryId);
        }
    }

    private newRegionRuntime(): RegionRuntime {
        return { status: 'idle', error: null, errorUrl: null, models: [], metashapeData: null,
            generation: 0, abortController: null, loadPromise: null, bounds: null };
    }

    private async loadSavedRegionState(): Promise<SavedRegionState> {
        const empty: SavedRegionState = { activeRegionId: null, selectedRegionIds: [] };
        if (this.projectConfig?.layout !== 'regional') return empty;
        const user = useCurrentUser();
        const email = user.value?.email;
        if (!email) return empty;
        try {
            const snapshot = await getDoc(doc(useFirestore(), 'users', email));
            if (!snapshot.exists()) return empty;
            const data = snapshot.data();
            const activeByProject = data.lastRegionByProject;
            const selectedByProject = data.selectedRegionsByProject;
            const activeRegionId = activeByProject && typeof activeByProject === 'object'
                ? (activeByProject as Record<string, unknown>)[this.projectName]
                : null;
            const selectedRegionIds = selectedByProject && typeof selectedByProject === 'object'
                ? (selectedByProject as Record<string, unknown>)[this.projectName]
                : null;
            return {
                activeRegionId: typeof activeRegionId === 'string' ? activeRegionId : null,
                selectedRegionIds: Array.isArray(selectedRegionIds)
                    ? selectedRegionIds.filter((value): value is string => typeof value === 'string')
                    : [],
            };
        } catch (error) {
            console.warn(`Failed to load saved regions for ${this.projectName}:`, error);
            return empty;
        }
    }

    private async saveActiveRegionId(regionId: string | null): Promise<void> {
        if (this.projectConfig?.layout !== 'regional') return;
        const user = useCurrentUser();
        const email = user.value?.email;
        if (!email) return;
        const userDoc = doc(useFirestore(), 'users', email);
        try {
            await updateDoc(userDoc, new FieldPath('lastRegionByProject', this.projectName), regionId);
        } catch {
            try {
                await setDoc(userDoc, {
                    lastRegionByProject: { [this.projectName]: regionId },
                }, { merge: true });
            } catch (error) {
                console.warn(`Failed to save active region ${regionId} for ${this.projectName}:`, error);
            }
        }
    }

    private async saveSelectedRegionIds(regionIds: string[]): Promise<void> {
        if (this.projectConfig?.layout !== 'regional') return;
        const user = useCurrentUser();
        const email = user.value?.email;
        if (!email) return;
        const userDoc = doc(useFirestore(), 'users', email);
        try {
            await updateDoc(userDoc, new FieldPath('selectedRegionsByProject', this.projectName), regionIds);
        } catch {
            try {
                await setDoc(userDoc, {
                    selectedRegionsByProject: { [this.projectName]: regionIds },
                }, { merge: true });
            } catch (error) {
                console.warn(`Failed to save selected regions for ${this.projectName}:`, error);
            }
        }
    }

    getRegionRuntime(regionId: string): RegionRuntime | null {
        this.regionStateVersion.value;
        return this.regionRuntimeById.get(regionId) ?? null;
    }

    setRegionError(regionId: string | null, message: string, url: string | null = null, fatal = true) {
        if (!regionId) return;
        const runtime = this.regionRuntimeById.get(regionId);
        if (!runtime) return;
        runtime.error = message;
        runtime.errorUrl = url;
        if (fatal) runtime.status = 'error';
        this.regionStateVersion.value++;
    }

    activeImageUrl(label: string): string | null {
        if (!this.projectConfig || !this.activeRegionId.value) return null;
        const region = this.availableRegions.find((item) => item.id === this.activeRegionId.value);
        return region ? imageUrl(this.projectConfig, region, label) : null;
    }

    activeImageUrlTif(label: string): string | null {
        if (!this.projectConfig || !this.activeRegionId.value) return null;
        const region = this.availableRegions.find((item) => item.id === this.activeRegionId.value);
        return region ? imageUrlTif(this.projectConfig, region, label) : null;
    }


    activeImageSensor(): ttMetashapeSensor | null {
        if (!this.metashapeData) return null;
        const camera = this.cameraIndex >= 0 && this.cameraIndex < this.metashapeData.cameras.length
            ? this.metashapeData.cameras[this.cameraIndex]
            : null;
        if (camera) {
            return this.metashapeData.sensors.find((sensor) => sensor.id === camera.sensorId) ?? null;
        }
        return this.metashapeData.sensors[0] ?? null;
    }

    activeImageSize(target = new THREE.Vector2()): THREE.Vector2 | null {
        const sensor = this.activeImageSensor();
        if (!sensor) return null;
        const camera = this.metashapeData?.cameras[this.cameraIndex];
        return ttUtils.orientedImageSize(sensor, camera?.orientation ?? 1, target);
    }

    activeImageModels(): THREE.Object3D[] {
        const activeRegionId = this.activeRegionId.value;
        if (!activeRegionId) return this.models;
        return this.regionRuntimeById.get(activeRegionId)?.models ?? [];
    }

    unprojectImagePoint(imageX: number, imageY: number): THREE.Vector3 | null {
        if (!this.metashapeData || this.cameraIndex < 0
            || this.cameraIndex >= this.metashapeData.cameras.length) return null;
        const camera = this.metashapeData.cameras[this.cameraIndex];
        const sensor = this.metashapeData.sensors.find((item) => item.id === camera.sensorId);
        if (!sensor) return null;
        return ttUtils.unprojectImagePoint(camera, sensor, this.activeImageModels(), imageX, imageY);
    }

    private acquireRegionLoadSlot(): Promise<() => void> {
        return new Promise((resolve) => {
            const grant = () => {
                this.activeRegionLoads++;
                let released = false;
                resolve(() => {
                    if (released) return;
                    released = true;
                    this.activeRegionLoads--;
                    this.regionLoadWaiters.shift()?.();
                });
            };
            if (this.activeRegionLoads < this.maxConcurrentRegionLoads) grant();
            else this.regionLoadWaiters.push(grant);
        });
    }

    async loadRegion(regionId: string): Promise<void> {
        if (!this.projectConfig || this.disposed) return;
        const region = this.availableRegions.find((item) => item.id === regionId);
        const runtime = this.regionRuntimeById.get(regionId);
        if (!region || !runtime || !region.enabled) return;
        if (runtime.status === 'loaded') return;
        if (runtime.loadPromise) return runtime.loadPromise;
        const generation = ++runtime.generation;
        runtime.status = 'loading';
        runtime.error = null;
        runtime.errorUrl = null;
        runtime.abortController?.abort();
        const abortController = new AbortController();
        runtime.abortController = abortController;
        this.regionStateVersion.value++;

        runtime.loadPromise = (async () => {
            const urls = region.modelPaths.map((path) => path.startsWith('blob:http') ? path : modelUrl(this.projectConfig!, region, path));
            let loadedModels: THREE.Object3D[] = [];
            const releaseLoadSlot = await this.acquireRegionLoadSlot();
            try {
                if (this.disposed || runtime.generation !== generation || !this.selectedRegionIds.value.includes(regionId)) return;
                // Keep the source GLBs' 25k JPEG atlases GPU-safe while retaining
                // substantially more useful detail than the previous 2k resize.
                const maxTextureSize = this.renderer?.capabilities.maxTextureSize ?? fallbackModelTextureSize;
                const textureAnisotropy = this.renderer?.capabilities.getMaxAnisotropy() ?? 1;
                for (const url of urls) {
                    loadedModels.push(await loadGlbAsset({
                        projectName: this.projectName,
                        url,
                        transformation: this.projectConfig!.meta.transformation,
                        applyLegacyProjectTransform: this.projectConfig!.layout === 'legacy',
                        convertGltfYUpToZUp: this.projectConfig!.layout === 'regional',
                        maxTextureSize,
                        maxTexturePixels: maxModelTexturePixels,
                        textureCompression: this.textureCompression,
                        cropUnusedTextureSpace: true,
                        textureAnisotropy,
                        signal: abortController.signal,
                    }));
                }
                if (this.disposed || runtime.generation !== generation || !this.selectedRegionIds.value.includes(regionId)) {
                    loadedModels.forEach(disposeObject);
                    return;
                }
                runtime.models = loadedModels;
                loadedModels.forEach((model) => {
                    model.userData.ttRegionId = regionId;
                    this.addModel(model);
                    if (this.debug) addAxisAndLabel(this.scene, model, 20, 10.7);
                });
                runtime.bounds = new THREE.Box3();
                loadedModels.forEach((model) => runtime.bounds!.union(new THREE.Box3().setFromObject(model, true)));
            } catch (error) {
                loadedModels.forEach(disposeObject);
                if ((error as DOMException)?.name === 'AbortError') return;
                if (this.disposed || runtime.generation !== generation || !this.selectedRegionIds.value.includes(regionId)) return;
                this.setRegionError(regionId, error instanceof Error ? error.message : String(error), urls[0] ?? null);
                return;
            } finally {
                releaseLoadSlot();
            }

            const xmlUrl = metadataUrl(this.projectConfig!, region);
            if (region.canActivate && xmlUrl) {
                try {
                    const data = await ttMetashapeParseUrl(
                        xmlUrl,
                        abortController.signal,
                        this.projectConfig!.layout === 'regional'
                            ? { useCalibratedCameraTransforms: true }
                            : { repairLegacyXml: true },
                    );
                    if (this.disposed || runtime.generation !== generation || !this.selectedRegionIds.value.includes(regionId)) return;
                    runtime.metashapeData = markRaw(data);
                } catch (error) {
                    if ((error as DOMException)?.name === 'AbortError') return;
                    this.setRegionError(regionId, `Region ${regionId}: ${error instanceof Error ? error.message : String(error)}`, xmlUrl);
                    return;
                }
            }
            if (runtime.generation === generation) {
                runtime.status = 'loaded';
                this.regionStateVersion.value++;
            }
        })().finally(() => {
            if (runtime.generation === generation) runtime.loadPromise = null;
        });
        return runtime.loadPromise;
    }

    async selectRegions(regionIds: string[], preferredActiveRegionId?: string | null): Promise<void> {
        const allowed = new Set(this.availableRegions.filter((region) => region.enabled).map((region) => region.id));
        const next = [...new Set(regionIds.filter((id) => allowed.has(id)))];
        const previous = [...this.selectedRegionIds.value];
        const removed = previous.filter((id) => !next.includes(id));
        const added = next.filter((id) => !previous.includes(id));
        this.selectedRegionIds.value = next;
        void this.saveSelectedRegionIds(next);
        removed.forEach((id) => this.unloadRegion(id));
        await Promise.allSettled(added.map((id) => this.loadRegion(id)));
        if (this.disposed) return;
        if (preferredActiveRegionId && next.includes(preferredActiveRegionId) && this.canActivateRegion(preferredActiveRegionId)) {
            await this.setActiveRegion(preferredActiveRegionId);
            return;
        }
        const addedActive = [...added].reverse().find((id) => this.canActivateRegion(id));
        if (addedActive) await this.setActiveRegion(addedActive);
        else if (!this.activeRegionId.value || !next.includes(this.activeRegionId.value)) {
            const fallback = next.find((id) => this.canActivateRegion(id)) ?? null;
            await this.setActiveRegion(fallback);
        }
    }

    canActivateRegion(regionId: string): boolean {
        const region = this.availableRegions.find((item) => item.id === regionId);
        const runtime = this.regionRuntimeById.get(regionId);
        return Boolean(region?.canActivate && runtime?.status === 'loaded' && runtime.metashapeData);
    }

    async setActiveRegion(regionId: string | null): Promise<void> {
        if (regionId && !this.selectedRegionIds.value.includes(regionId)) return;
        if (regionId && !this.canActivateRegion(regionId)) {
            await this.loadRegion(regionId);
            if (!this.canActivateRegion(regionId)) return;
        }
        if (this.activeRegionId.value === regionId && this.metashapeData) return;
        this.clearActiveRegionHelpers();
        this.activeRegionId.value = regionId;
        if (!regionId) {
            void this.saveActiveRegionId(null);
            return;
        }
        const data = this.regionRuntimeById.get(regionId)?.metashapeData;
        if (!data) return;
        this.metashapeData = data;
        const initializeView = !this.initializedView;
        ttCreateCamerasAndSetViewport(this, data, initializeView);
        this.initializedView = true;
        if (initializeView) this.zoomToSceneExtents();
        this.refreshImageViewport();
        void this.saveActiveRegionId(regionId);
    }

    private clearActiveRegionHelpers() {
        for (const camera of this.msCameras) {
            this.scene.remove(camera);
            camera.geometry.dispose();
            const material = camera.material;
            if (Array.isArray(material)) material.forEach((m) => m.dispose());
            else material.dispose();
        }
        for (const frustum of this.msCameraFrustums) {
            this.scene.remove(frustum);
            frustum.geometry.dispose();
            const material = frustum.material;
            if (Array.isArray(material)) material.forEach((m) => m.dispose());
            else material.dispose();
        }
        this.msCameraFrustums.length = 0;
        this.msCameras.length = 0;
        if (!this.hasCustomTrajectories) this.clearTrajectory();
        for (const line of this.drawnLinesImage) {
            this.imageScene.remove(line);
            line.geometry.dispose();
        }
        this.drawnLinesImage = [];
        this.cameraIndex = -1;
        this.imageLabel.value = '';
        this.imageMaterial?.map?.dispose();
        if (this.imageMaterial) {
            this.imageMaterial.map = null;
            this.imageMaterial.needsUpdate = true;
        }
        this.metashapeData = null;
    }

    private unloadRegion(regionId: string) {
        const runtime = this.regionRuntimeById.get(regionId);
        if (!runtime) return;
        runtime.generation++;
        runtime.abortController?.abort();
        runtime.abortController = null;
        if (this.activeRegionId.value === regionId) {
            this.clearActiveRegionHelpers();
            this.activeRegionId.value = null;
        }
        runtime.models.forEach((model) => {
            this.removeModel(model);
            disposeObject(model);
        });
        runtime.models = [];
        runtime.bounds = null;
        runtime.metashapeData = null;
        runtime.status = 'idle';
        runtime.error = null;
        runtime.errorUrl = null;
        runtime.loadPromise = null;
        this.regionStateVersion.value++;
    }

    getProjectPointCloudRuntime(pointCloudId: string): ProjectPointCloudRuntime | null {
        return this.projectPointCloudRuntimeById.get(pointCloudId) ?? null;
    }

    async loadPointCloud(pointCloudId: string): Promise<void> {
        if (!this.projectConfig || this.disposed) return;
        const pointCloud = this.projectPointClouds.find((item) => item.id === pointCloudId);
        const runtime = this.projectPointCloudRuntimeById.get(pointCloudId);
        if (!pointCloud || !runtime || !pointCloud.enabled) return;
        if (runtime.status === 'loaded') return;
        //if (runtime.loadPromise) return runtime.loadPromise;
        pointCloud.loading = true;
        runtime.status = 'loading';
        runtime.error = null;
        runtime.errorUrl = null;
        runtime.abortController?.abort();
        const abortController = new AbortController();
        runtime.abortController = abortController;
        runtime.loadPromise = (async () => {
            const releaseLoadSlot = await this.acquireRegionLoadSlot();
            try {
                if (this.disposed || (!this.selectedPointCloudIds.value.includes(pointCloudId) 
                    && this.selectedPointCloudIds.value.length > 0)) return;
                const pointCloudModel = await loadPlyAsset({
                    projectName: this.projectName,
                    url: pointCloud.path,
                    transformation: this.projectConfig!.meta.transformation,
                    convertPlyYUpToZUp: false,
                    signal: abortController.signal,
                });
                if (this.disposed || (!this.selectedPointCloudIds.value.includes(pointCloudId) 
                    && this.selectedPointCloudIds.value.length > 0)) {
                    disposeObject(pointCloudModel);
                    return;
                }
                pointCloudModel.geometry.computeBoundingBox();
                this.scene.add(pointCloudModel);
                runtime.pointCloud = pointCloudModel;
                runtime.bounds = pointCloudModel.geometry.boundingBox ?? null;
            } catch (error) {
                if(runtime.pointCloud)
                    disposeObject(runtime.pointCloud);
                if ((error as DOMException)?.name === 'AbortError') return;
                runtime.error = error instanceof Error ? error.message : String(error);
                return;
            } finally {
                pointCloud.loaded = runtime.pointCloud !== null;
                pointCloud.loading = false;
                pointCloud.visible = runtime.pointCloud !== null;
                releaseLoadSlot();
            }
        })();
        return runtime.loadPromise;
    }

    async togglePointCloud(pointCloudId: string): Promise<void> {
        const unload = this.selectedPointCloudIds.value.includes(pointCloudId);
        if (unload)
            return this.selectPointClouds(this.selectedPointCloudIds.value.filter((id) => id !== pointCloudId));
        return this.selectPointClouds([...this.selectedPointCloudIds.value , pointCloudId ]);
    }

    async selectPointClouds(pointCloudIds: string[]): Promise<void> {
        const allowed = new Set(this.projectPointClouds.filter((pointCloud) => pointCloud.enabled).map((pointCloud) => pointCloud.id));
        const next = [...new Set(pointCloudIds.filter((id) => allowed.has(id)))];
        const previous = [...this.selectedPointCloudIds.value];
        const removed = previous.filter((id) => !next.includes(id));
        const added = next.filter((id) => !previous.includes(id));
        this.selectedPointCloudIds.value = next;
        void this.saveSelectedPointCloudIds(next);
        removed.forEach((id) => this.unloadPointCloud(id));
        added.forEach((id) => this.loadPointCloud(id));
    }    

    private async saveSelectedPointCloudIds(regionIds: string[]): Promise<void> {
        if (this.projectConfig?.layout !== 'regional') return;
        const user = useCurrentUser();
        const email = user.value?.email;
        if (!email) return;
        const userDoc = doc(useFirestore(), 'users', email);
        try {
            await updateDoc(userDoc, new FieldPath('selectedPointCloudsByProject', this.projectName), regionIds);
        } catch {
            try {
                await setDoc(userDoc, {
                    selectedPointCloudsByProject: { [this.projectName]: regionIds },
                }, { merge: true });
            } catch (error) {
                console.warn(`Failed to save selected regions for ${this.projectName}:`, error);
            }
        }
    }

    private unloadPointCloud(pointCloudId: string) {
        const pointCloud = this.projectPointClouds.find((item) => item.id === pointCloudId);
        if (!pointCloud) return;
        const runtime = this.projectPointCloudRuntimeById.get(pointCloud.id);
        if (!runtime) return;

        runtime.abortController?.abort();
        runtime.abortController = null;

        if(runtime.pointCloud) {
            this.scene.remove(runtime.pointCloud);
            disposeObject(runtime.pointCloud);
        }

        runtime.pointCloud = null;
        runtime.bounds = null;
        runtime.status = 'idle';
        runtime.error = null;
        runtime.errorUrl = null;
        runtime.loadPromise = null;
        pointCloud.loaded = false;
        pointCloud.loading = false;
        pointCloud.visible = false;
    }

    onUnmounted() {
        this.disposed = true;
        if (this.keydownFn) document.removeEventListener('keydown', this.keydownFn);

        if (this.canvas) {
            if (this.mainCanvasClickFn) {
                this.canvas.removeEventListener("click", this.mainCanvasClickFn);
            }
            if (this.mainCanvasDblClickFn) {
                this.canvas.removeEventListener("dblclick", this.mainCanvasDblClickFn);
            }
            if (this.mainCanvasAuxClickFn) {
                this.canvas.removeEventListener("auxclick", this.mainCanvasAuxClickFn);
            }
            if (this.mainCanvasPointerMoveFn) {
                this.canvas.removeEventListener('pointermove', this.mainCanvasPointerMoveFn);
            }
            if (this.mainCanvasPointerLeaveFn) {
                this.canvas.removeEventListener('pointerleave', this.mainCanvasPointerLeaveFn);
            }
        }
        if (this.cursorFrameId !== null) cancelAnimationFrame(this.cursorFrameId);
        this.cursorFrameId = null;

        if (this.imageCanvas) {
            if (this.imageCanvasDblClickFn) {
                this.imageCanvas.removeEventListener("dblclick", this.imageCanvasDblClickFn);
            }
            if (this.imageCanvasContextMenuFn) {
                this.imageCanvas.removeEventListener('contextmenu', this.imageCanvasContextMenuFn);
            }
            if (this.imageCanvasAuxClickFn) {
                this.imageCanvas.removeEventListener('auxclick', this.imageCanvasAuxClickFn);
            }
        }
        for (const id of [...this.regionRuntimeById.keys()]) this.unloadRegion(id);
        this.clearActiveRegionHelpers();
        this.clearTrajectory();
        this.controls?.dispose();
        this.orthoControls?.dispose();
        this.imageMesh.geometry?.dispose();
        this.imageMaterial?.dispose();
        this.imageMaterial = null;
    }

    beginCommand(command: ttCmd.CmdBase) {
        if (this.currentCommand.value) {
            console.log('command already running:' + this.currentCommand.value);
            return;
        }
        this.undoStack.redo.length = 0;
        this.currentCommand.value = command;
        this.currentCommand.value.onBegin();
    }

    endCommand() {
        if (this.currentCommand.value)
            this.currentCommand.value.onEnd();
        this.undoStack.undo.push(this.currentCommand.value);
        if (this.undoStack.undo.length > this.undoStack.max)
            this.undoStack.undo.slice(0, 1);
        this.currentCommand.value = null;
    }

    cancelCommand() {
        if (this.currentCommand.value)
            this.currentCommand.value.onCancel();
        this.currentCommand.value = null;
    }

    undo() {
        if (this.undoStack.undo.length === 0)
            return;
        const cmd = this.undoStack.undo.pop();
        if (cmd) {
            this.undoStack.redo.push(cmd);
            cmd.undo();
        }
    }

    redo() {
        if (this.undoStack.redo.length === 0)
            return;
        const cmd = this.undoStack.redo.pop();
        if (cmd) {
            this.undoStack.undo.push(cmd);
            cmd.redo();
        }
    }

    addModel(model: THREE.Object3D) {
        this.models.push(model);
        this.scene.add(model);
    }

    removeModel(model: THREE.Object3D) {
        this.models = this.models.filter((e: THREE.Object3D) => e !== model);
        this.scene.remove(model);
    }

    addLine(line: Polyline) {
        if (this.lines.includes(line)) {
            return;
        }
        if (line.count() === 0) {
            console.log("Empty line");
        }
        this.scene.add(line.obj);
        if (line.severity < 0) {
            line.severity = this.currentSeverity;
        }
        line.setMaterial(line.severity);
        this.lines.push(line);
        if (line.name.length === 0) {
            line.name = "Line - " + line.obj.id;
        }
        if (line.count() === 2) {
            if (line.isMarker()) {
                createSVGElement(this, line);
            }
        }
    }

    private zoomToBox(box: THREE.Box3, fitOffset = 1.02) {
        if (!this.controls || !this.camera) {
            return;
        }
        if (box.isEmpty()) {
            console.warn("Bounding box is empty; nothing to zoom.");
            return;
        }

        box.getSize(this.boxSize);
        box.getCenter(this.boxCenter);

        // Keep the camera fractionally off the exact Z pole. The view remains
        // visually top-down while OrbitControls retains a stable screen-up axis.
        const tilt = THREE.MathUtils.degToRad(0.01);
        const sinTilt = Math.sin(tilt);
        const cosTilt = Math.cos(tilt);
        const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov * 0.5) * Math.max(this.camera.aspect, 0.0001));

        // Project the world-aligned bounds onto the almost-horizontal screen.
        // Z contributes slightly to screen height because of the stabilizing tilt.
        const projectedWidth = this.boxSize.x;
        const projectedHeight = this.boxSize.y * cosTilt + this.boxSize.z * sinTilt;
        const halfDepth = 0.5 * (this.boxSize.z * cosTilt + this.boxSize.y * sinTilt);
        const minimumSpan = Math.max(this.boxSize.length() * 1e-6, 0.01);
        const widthDistance = Math.max(projectedWidth, minimumSpan) * 0.5 / Math.tan(horizontalFov * 0.5);
        const heightDistance = Math.max(projectedHeight, minimumSpan) * 0.5 / Math.tan(verticalFov * 0.5);
        const clearDistance = Math.max(widthDistance, heightDistance) * Math.max(fitOffset, 1);
        const cameraDistance = clearDistance + halfDepth;

        this.camera.up.set(0, 0, 1);
        this.camera.zoom = 1;
        this.camera.position.set(
            this.boxCenter.x,
            this.boxCenter.y - cameraDistance * sinTilt,
            this.boxCenter.z + cameraDistance * cosTilt,
        );
        this.controls.target.copy(this.boxCenter);

        this.camera.near = Math.max(0.01, clearDistance / 1000);
        const requiredFarDistance = cameraDistance + halfDepth + clearDistance;
        this.camera.far = Math.max(
            this.camera.near + 1,
            requiredFarDistance * mainCameraFarDistanceMultiplier,
        );
        this.camera.updateProjectionMatrix();
        this.controls.update();
        this.updateOnControlChange();
    }

    zoomToObjects(objects: THREE.Object3D[], fitOffset = 1.08) {
        this.scene.updateMatrixWorld(true);
        this.box3Helper.makeEmpty();
        for (const object of objects) {
            const objectBox = new THREE.Box3().setFromObject(object, true);
            if (!objectBox.isEmpty()) this.box3Helper.union(objectBox);
        }
        this.zoomToBox(this.box3Helper, fitOffset);
    }

    zoomToSceneExtents(fitOffset = 1.02) {
        this.scene.updateMatrixWorld(true);
        this.box3Helper.makeEmpty();
        this.scene.traverseVisible((object) => {
            const renderable = object as THREE.Object3D & { geometry?: THREE.BufferGeometry };
            if (!renderable.geometry) return;
            const objectBox = new THREE.Box3().setFromObject(object, true);
            if (!objectBox.isEmpty()) this.box3Helper.union(objectBox);
        });
        this.zoomToBox(this.box3Helper, fitOffset);
    }

    removeLine(line: Polyline) {
        const index = this.lines.indexOf(line);
        if (index > -1) {
            this.lines.splice(index, 1);
            this.scene.remove(line.obj);
        }
        if (line.svg) {
            const element = document.getElementById("mainCanvasDivId");
            if (element) {
                element.removeChild(line.svg);
                line.svg = null;
            }
        }
    }

    updateOnControlChange() {
        if (!this.controls)
            return;
        const element = document.getElementById("mainCanvasDivId");
        if (!element) {
            console.log("No mainCanvasDivId");
            return;
        }
        let wtm = new THREE.Matrix4();
        const v = new THREE.Vector3();
        const vt = new THREE.Vector3();
        const width = element.clientWidth;
        const height = element.clientHeight;
        this.camera.updateMatrix();
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
        wtm.copy(this.camera.matrixWorld);
        wtm = wtm.invert();

        if (this.trajectoryLength.value > 0) {
            closestPointToTrajectory(this.trajectory, this.camera.position, this.trajectoryPos.value);
            this.syncHorizontalStation();
        } else {
            this.trajectoryPos.value.w = 0;
            this.trajectoryHorizontalPos.value = 0;
        }

        //Move svg - markers
        for (const line of this.lines) {
            if (line.svg) {
                if (!line.obj.visible) {
                    line.svg.style.display = "none";
                    continue;
                }
                line.vertexAt(0, v);
                vt.set(v.x, v.y, v.z);
                vt.applyMatrix4(wtm);
                v.project(this.camera);
                if (vt.z < 0.0) {
                    const x = (v.x * 0.5 + 0.5) * width;
                    const y = (-v.y * 0.5 + 0.5) * height;
                    line.svg.style.left = `${x}px`;
                    line.svg.style.top = `${y}px`;
                    line.svg.style.transform = 'translate(-50%, -100%) scale(1)';
                    line.svg.custom_x = x;
                    line.svg.custom_y = y;
                    line.svg.style.display = "";
                }
                else {
                    line.svg.style.display = "none";
                }
            }
        }
    }

    private updateCursorWorldPosition(clientX: number, clientY: number) {
        if (!this.canvas) return;
        const bounds = this.canvas.getBoundingClientRect();
        if (clientX < bounds.left || clientX > bounds.right || clientY < bounds.top || clientY > bounds.bottom) {
            this.cursorWorldPosition.valid = false;
            return;
        }

        const mouse = ttUtils.toNormalizedScreenCoord(clientX, clientY, this.canvas);
        this.raycaster.setFromCamera(mouse, this.camera);
        const visibleModels = this.models.filter((model) => model.visible);
        const modelIntersection = this.raycaster.intersectObjects(visibleModels, true)
            .find((intersection) => intersection.object.visible);
        let point = modelIntersection?.point;
        let source: CursorWorldPosition['source'] = 'model';
        if (!point) {
            source = 'ground';
            point = this.raycaster.ray.intersectPlane(this.groundPlane, this.cursorPoint) ?? undefined;
        }
        if (!point) {
            this.cursorWorldPosition.valid = false;
            return;
        }

        this.cursorWorldPosition.x = point.x;
        this.cursorWorldPosition.y = point.y;
        this.cursorWorldPosition.z = point.z;
        this.cursorWorldPosition.source = source;
        this.cursorWorldPosition.valid = true;
    }


    setMainCanvas(mainCanvas: HTMLElement, renderer: THREE.WebGLRenderer) {
        this.renderer      = renderer;
        this.canvas        = mainCanvas;
        this.viewHelper    = new ViewHelper(this.camera, mainCanvas);
        this.camera.up.set(0, 0, 1);
        this.camera.aspect = mainCanvas.clientWidth / mainCanvas.clientHeight;
        this.camera.updateProjectionMatrix();
        this.mainCanvasClickFn = (event) => mainCanvasClickHandler(this, event);
        this.mainCanvasDblClickFn = (event) => mainCanvasDblClickHandler(this, event);
        this.mainCanvasAuxClickFn = (event) => {
            if (event.button === 1 && event.detail === 2) {
                this.zoomToSceneExtents();
            }
        };
        this.mainCanvasPointerMoveFn = (event) => {
            this.cursorClientX = event.clientX;
            this.cursorClientY = event.clientY;
            if (this.cursorFrameId !== null) return;
            this.cursorFrameId = requestAnimationFrame(() => {
                this.cursorFrameId = null;
                this.updateCursorWorldPosition(this.cursorClientX, this.cursorClientY);
            });
        };
        this.mainCanvasPointerLeaveFn = () => {
            this.cursorWorldPosition.valid = false;
        };
        this.canvas.addEventListener("click", this.mainCanvasClickFn);
        this.canvas.addEventListener("dblclick", this.mainCanvasDblClickFn);
        this.canvas.addEventListener("auxclick", this.mainCanvasAuxClickFn);
        this.canvas.addEventListener('pointermove', this.mainCanvasPointerMoveFn);
        this.canvas.addEventListener('pointerleave', this.mainCanvasPointerLeaveFn);
        this.keydownFn = (event) => this.keyboardHandler(event);
        document.addEventListener('keydown', this.keydownFn, false);

        this.controls = new OrbitControls(this.camera, mainCanvas);
        this.controls.enableDamping = false;
        this.controls.listenToKeyEvents(window);
        this.controls.addEventListener('change', (event) => {
            this.updateOnControlChange();
        });
        //this.controls.screenSpacePanning = false;
        this.controls.keyPanSpeed = 20;
        this.controls.mouseButtons = {
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.ROTATE
        };
        if (this.stats) {
            document.body.append(this.stats.dom);
        }
    }

    setImageCanvas(imageCanvas: HTMLElement, renderer: THREE.WebGLRenderer) {
        if (this.imageCanvas) {
            if (this.imageCanvasDblClickFn) {
                this.imageCanvas.removeEventListener("dblclick", this.imageCanvasDblClickFn);
            }
            if (this.imageCanvasContextMenuFn) {
                this.imageCanvas.removeEventListener('contextmenu', this.imageCanvasContextMenuFn);
            }
            if (this.imageCanvasAuxClickFn) {
                this.imageCanvas.removeEventListener('auxclick', this.imageCanvasAuxClickFn);
            }
        }
        this.orthoControls?.dispose();
        this.imageRenderer = renderer;
        this.imageCanvas   = imageCanvas;
        if (this.imageCanvas) {
            this.imageCanvasDblClickFn = (event) => imageCanvasDblClickHandler(this, event);
            this.imageCanvasContextMenuFn = (event) => event.preventDefault();
            this.imageCanvasAuxClickFn = (event) => {
                if (event.button === 1) event.preventDefault();
            };
            this.imageCanvas.addEventListener("dblclick", this.imageCanvasDblClickFn);
            this.imageCanvas.addEventListener('contextmenu', this.imageCanvasContextMenuFn);
            this.imageCanvas.addEventListener('auxclick', this.imageCanvasAuxClickFn);
            this.imageScene.background = new THREE.Color(0x111111);
            const imageSize = this.activeImageSize();
            const imwidth = imageSize?.x ?? 1;
            const imheight = imageSize?.y ?? 1;

            this.orthoControls = new OrbitControls(this.orthoCamera, this.imageCanvas);
            this.orthoControls.target.set(this.orthoCamera.position.x, this.orthoCamera.position.y, 0.0);
            this.orthoControls.enableRotate = false;
            this.orthoControls.enablePan = true;
            this.orthoControls.enableZoom = true;
            this.orthoControls.screenSpacePanning = true;
            this.orthoControls.minZoom = 1.0;
            this.orthoControls.mouseButtons = {
                MIDDLE: THREE.MOUSE.PAN,
                RIGHT: THREE.MOUSE.PAN,
            };
            this.orthoControls.addEventListener('change', () => this.clampImagePan());

            if (!this.imageMaterial) this.imageMaterial = new THREE.MeshLambertMaterial({ side: THREE.DoubleSide });
            if (this.imageMesh.parent) this.imageScene.remove(this.imageMesh);
            this.imageMesh.geometry?.dispose();
            const geometry = new THREE.PlaneGeometry(imwidth, imheight);
            this.imageMesh = new THREE.Mesh(geometry, this.imageMaterial);
            this.imageMesh.position.set(imwidth * 0.5, imheight * 0.5, 0.0);
            this.imageMesh.up.set(0.0, 1.0, 0.0);
            this.imageScene.add(this.imageMesh);
            this.resetImageViewport();
            projectLinesOnImage(this, this.cameraIndex);
        }
    }

    detachImageCanvas() {
        this.currentCommand.value?.onImageCanvasChanged(null, undefined);
        if (this.imageCanvas) {
            if (this.imageCanvasDblClickFn) this.imageCanvas.removeEventListener('dblclick', this.imageCanvasDblClickFn);
            if (this.imageCanvasContextMenuFn) this.imageCanvas.removeEventListener('contextmenu', this.imageCanvasContextMenuFn);
            if (this.imageCanvasAuxClickFn) this.imageCanvas.removeEventListener('auxclick', this.imageCanvasAuxClickFn);
        }
        this.orthoControls?.dispose();
        this.orthoControls = undefined;
        this.imageCanvas = null;
        this.imageRenderer = null;
    }

    refreshImageViewport(flip: boolean = false) {
        const imageSize = this.activeImageSize();
        if (!imageSize) return;
        const imwidth = imageSize.x;
        const imheight = imageSize.y;
        const aspect = this.imageCanvas ? this.imageCanvas.clientWidth / Math.max(1, this.imageCanvas.clientHeight) : imwidth / imheight;
        const imageAspect = imwidth / Math.max(1, imheight);
        const viewWidth = aspect >= imageAspect ? imwidth : imheight * aspect;
        const viewHeight = aspect >= imageAspect ? imwidth / aspect : imheight;
        const sensorChanged = this.imageViewportSensorSize.x !== imwidth || this.imageViewportSensorSize.y !== imheight;
        this.imageViewportSensorSize.set(imwidth, imheight);

        this.orthoCamera.left = -viewWidth * 0.5;
        this.orthoCamera.right = viewWidth * 0.5;
        this.orthoCamera.top = viewHeight * 0.5;
        this.orthoCamera.bottom = -viewHeight * 0.5;
        this.orthoCamera.near = -1000;
        this.orthoCamera.far = 1000;
        if (sensorChanged) {
            this.orthoCamera.zoom = 1;
            this.orthoCamera.position.set(imwidth * 0.5, imheight * 0.5, 1);
            this.orthoControls?.target.set(imwidth * 0.5, imheight * 0.5, 0);
        } else {
            this.orthoCamera.zoom = Math.max(1, this.orthoCamera.zoom);
        }
        if(flip)
            this.orthoCamera.up.set(0, -1, 0);
        else
            this.orthoCamera.up.set(0, 1, 0);
        this.imageMesh.rotation.set(0, 0, 0);
        this.orthoCamera.updateProjectionMatrix();
        if (this.imageMesh.parent) {
            this.imageMesh.geometry.dispose();
            this.imageMesh.geometry = new THREE.PlaneGeometry(imwidth, imheight);
            this.imageMesh.position.set(imwidth * 0.5, imheight * 0.5, 0);
        }
        this.clampImagePan();
        this.orthoControls?.update();
    }

    refreshProjectedLines() {
        if (this.imageCanvas && this.cameraIndex >= 0) {
            projectLinesOnImage(this, this.cameraIndex);
        }
    }

    resetImageViewport(flip: boolean = false) {
        const imageSize = this.activeImageSize();
        if (!imageSize) return;
        this.orthoCamera.zoom = 1;
        this.orthoCamera.position.set(imageSize.x * 0.5, imageSize.y * 0.5, 1);
        this.orthoControls?.target.set(imageSize.x * 0.5, imageSize.y * 0.5, 0);
        this.refreshImageViewport(flip);
    }

    private clampImagePan() {
        const imageSize = this.activeImageSize();
        if (!imageSize || !this.orthoControls) return;
        const zoom = Math.max(1, this.orthoCamera.zoom);
        if (this.orthoCamera.zoom !== zoom) {
            this.orthoCamera.zoom = zoom;
            this.orthoCamera.updateProjectionMatrix();
        }
        const halfViewWidth = (this.orthoCamera.right - this.orthoCamera.left) / (2 * zoom);
        const halfViewHeight = (this.orthoCamera.top - this.orthoCamera.bottom) / (2 * zoom);
        const target = this.orthoControls.target;
        const clampedX = THREE.MathUtils.clamp(target.x, halfViewWidth, imageSize.x - halfViewWidth);
        const clampedY = THREE.MathUtils.clamp(target.y, halfViewHeight, imageSize.y - halfViewHeight);
        const deltaX = clampedX - target.x;
        const deltaY = clampedY - target.y;
        target.set(clampedX, clampedY, 0);
        this.orthoCamera.position.x += deltaX;
        this.orthoCamera.position.y += deltaY;
        this.orthoCamera.position.z = 1;
    }

    toJSON(): object {
        const linesStr: object[] = [];
        for (let l of this.lines) {
            if (Object.keys(l).length !== 0) {
                linesStr.push(l.toJSON());
            }
        }
        return {
            userData: {
                camera: this.camera.toJSON(),
                controlsTarget: this.controls.target.toArray(),
                lastProject: this.projectName,
            },
            data: {
                lines: linesStr
            }
        };
    }

    async fromJSON(json: object) {
        let loader = new THREE.ObjectLoader();
        let camera = await loader.parseAsync(json.userData.camera);
        if (json.controlsTarget !== undefined) {
            this.controls.target.fromArray(json.userData.controlsTarget);
        }
        this.camera.copy(camera);
        this.camera.up.set(0, 0, 1);
        this.camera.updateProjectionMatrix();
        this.controls.update();

        this.lines.length = 0;
        for (let l of json.data.lines) {
            if (Object.keys(l).length === 0)
                continue;
            let ls = new Polyline();
            ls.fromJSON(l);
            this.lines.push(ls);
            this.scene.add(ls.obj);
        }
    }

    objectByUUID(uuid: string) {
        return this.scene.getObjectByProperty('uuid', uuid);
    }

    saveLocal() {
        let jsonData = JSON.stringify(this.toJSON());
        localStorage.setItem('appData', jsonData);
    }

    async saveServer() {
        const db = useFirestore();
        const user = useCurrentUser();
        const json = this.toJSON();
        const projectDoc = doc(db, "projects", this.projectName);
        const userDoc = doc(db, "users", user.value!.email);
        const fbUserDoc = updateDoc(userDoc, json.userData);
        const fbDataDoc = updateDoc(projectDoc, { lines: json.data.lines});
        await fbUserDoc;
        await fbDataDoc;
    }

    importPointCloudFromFile(file: File): Promise<void> {
        const pointCloudEntry: ProjectPointCloud = {
            path: URL.createObjectURL(file),
            name: file.name,
            custom: {},
            id: crypto.randomUUID(),
            subtitle: '',
            loaded: false,
            visible: false,
            loading: false,
            enabled: true,
        }
        this.projectPointClouds.push(pointCloudEntry);
        const runtime: ProjectPointCloudRuntime = {
            pointCloud: null,
            bounds: null,
            status: 'idle',
            error: null,
            errorUrl: null,
            loadPromise: null,
            abortController: null,
        }
        this.projectPointCloudRuntimeById.set(pointCloudEntry.id, runtime);
        try {
            return this.loadPointCloud(pointCloudEntry.id);
        } catch (error) {
            console.error(`Failed to load point cloud ${pointCloudEntry.name}:`, error);
            return Promise.reject(error);
        }
    }

    importGlbModelFromFile(file: File): Promise<void> {
        return new Promise((resolve, reject) => {

            // create new region for holding the local GLB model, if it doesn't already exist
            var region = this.availableRegions.find((region) => region.id === 'local');
            if (!region) {
                const newRegion = {
                    id: crypto.randomUUID(),
                    name: 'Local Model',
                    label: file.name,
                    modelPath: 'local',
                    modelPaths: [URL.createObjectURL(file)],
                    enabled: true,
                    canActivate: true,
                };
                this.availableRegions.push(newRegion);
                this.regionRuntimeById.set(newRegion.id, this.newRegionRuntime());

            }
            const localModelRegion = this.availableRegions.find((region) => region.id === 'local')!;
            //add to selected regions if not already selected
            if (!this.selectedRegionIds.value.includes(localModelRegion.id)) {
                this.selectedRegionIds.value.push(localModelRegion.id);
            }
            this.loadRegion(localModelRegion.id).then(() => {
                this.setActiveRegion(localModelRegion.id).then(() => {
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            }).catch((error) => {
                this.availableRegions = this.availableRegions.filter((region) => region.id !== localModelRegion.id);
                this.regionRuntimeById.delete(localModelRegion.id);
                reject(error);
            });
        });
    }

    async loadServer() {
        let loader = new THREE.ObjectLoader();
        const db = useFirestore();
        const user = useCurrentUser();
        const projectDoc = doc(db, "projects", this.projectName);
        const userDoc = doc(db, "users", user.value!.email);

        const [fbUserDoc, fbDataDoc] = await Promise.all([
            getDoc(userDoc),
            getDoc(projectDoc),
        ]);
        if (fbUserDoc.exists()) {
            const userData = fbUserDoc.data();
            if (userData.lastProject === this.projectName && userData.camera) {
                try {
                    const camera = await loader.parseAsync(userData.camera);
                    if (userData.controlsTarget !== undefined) {
                        this.controls.target.fromArray(userData.controlsTarget);
                    }
                    this.camera.copy(camera);
                    this.camera.up.set(0, 0, 1);
                    this.camera.updateProjectionMatrix();
                    this.controls.update();
                } catch (error) {
                    console.warn(`Failed to restore saved camera for ${this.projectName}; loading project lines anyway.`, error);
                }
            }
        }

        if (fbDataDoc.exists()) {
            this.lines.length = 0;
            if (fbDataDoc && fbDataDoc.data() && fbDataDoc.data().lines) {
                for (let l of fbDataDoc.data().lines) {
                    if (Object.keys(l).length === 0)
                        continue;
                    try {
                        let ls = new Polyline();
                        ls.fromJSON(l);
                        this.lines.push(ls);
                        this.scene.add(ls.obj);
                        if (ls.name.length === 0) {
                            ls.name = "Line - " + ls.obj.id;
                        }
                    } catch (error) {
                        console.warn(`Skipping an invalid saved line in ${this.projectName}.`, error);
                    }
                }
            }
        }
        createSVGElements(this);
    }

    keyboardHandler(event: KeyboardEvent) {
        if (event.target && event.target.nodeName !== "BODY") {
            return true;
        }
        const keyName = event.code;
        switch (keyName) {
            case 'Escape': {
                if (this.currentCommand.value !== null) {
                    this.cancelCommand();
                }
                else {
                    this.selection.clear();
                }
                break;
            }
            case 'Enter': {
                if (this.currentCommand.value !== null) {
                    this.endCommand();
                }
                break;
            }
            case 'KeyP': {
                this.createPolylineCommand();
                break;
            }
            case 'KeyM': {
                this.createMarkerCommand();
                break;
            }
            case 'KeyD': {
                this.getDistanceCommand();
                break;
            }
            case 'KeyU': {
                this.undo();
                break;
            }
            case 'KeyR': {
                this.redo();
                break;
            }
            case 'Delete': {
                if (this.currentCommand.value === null) {
                    let command = new ttCmd.DeleteLinesCmd(this);
                    this.beginCommand(command);
                    this.endCommand();
                }
                break;
            }
        }
    }

    createPolylineCommand() {
        if (this.currentCommand.value === null) {
            let command = new ttCmd.CreatePolylineCmd(this);
            this.beginCommand(command);
        }
    }

    createMarkerCommand() {
        if (this.currentCommand.value === null) {
            let command = new ttCmd.CreateMarkerCommand(this);
            this.beginCommand(command);
        }
    }

    getDistanceCommand() {
        if (this.currentCommand.value === null) {
            let command = new ttCmd.GetDistanceCmd(this);
            this.beginCommand(command);
            this.onDistanceCommandStarted?.();
        }
    }

    zoomTo(object: THREE.Object3D) {
        const box = new THREE.Box3();
        box.setFromObject(object, false);
        const boxCenter = new THREE.Vector3();
        box.getCenter(boxCenter);
        viewer?.controls?.target.set(boxCenter.x, boxCenter.y, boxCenter.z);
        if (this.trajectoryLength.value > 0) {
            const closestPointOnTrajectory = closestPointToTrajectory(this.trajectory, boxCenter, null);
            viewer?.controls?.object.position.copy(closestPointOnTrajectory);
        }
        viewer?.controls?.update();

        if (viewer?.imageCanvas) {
        }
    }

    private activateClosestLoadedRegion(point: THREE.Vector3) {
        if (!this.hasCustomTrajectories || this.projectConfig?.layout !== 'regional' || this.currentCommand.value) return;
        let closestRegionId: string | null = null;
        let closestDistance = Infinity;
        for (const regionId of this.selectedRegionIds.value) {
            const runtime = this.regionRuntimeById.get(regionId);
            if (runtime?.status !== 'loaded' || !runtime.metashapeData || !runtime.bounds || runtime.bounds.isEmpty()) continue;
            const distance = runtime.bounds.distanceToPoint(point);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestRegionId = regionId;
            }
        }
        if (closestRegionId && closestRegionId !== this.activeRegionId.value) {
            void this.setActiveRegion(closestRegionId);
        }
    }

    setStation(value: number) {
        const length = this.trajectoryLength.value;
        if (length <= 0 || !this.controls) return;
        value = Math.max(0, Math.min(length, Number(value) || 0));
        const position = value / length;
        const newPos = this.trajectory.getPoint(position);
        const dir = new THREE.Vector3();
        dir.subVectors(this.controls.target, this.camera.position);
        this.camera.position.copy(newPos);

        const raycaster = new THREE.Raycaster();
        if (dir.lengthSq() > 0) dir.normalize();
        raycaster.set(newPos, dir);
        const intersects = raycaster.intersectObjects(this.models, true);
        if (intersects.length) {
            this.controls?.target.copy(intersects[0].point);
        }
        else {
            this.controls?.target.copy(dir.add(newPos));
        }
        this.trajectoryPos.value.set(newPos.x, newPos.y, newPos.z, value);
        this.syncHorizontalStation();
        this.activateClosestLoadedRegion(newPos);
        this.controls.update();
    }

    private async resolveRegionImageContext(imageName: string, requestedRegionId?: string): Promise<RegionImageContext> {
        const label = imageName.replace(/\.[^.]+$/, '');
        if (requestedRegionId) {
            const region = this.availableRegions.find((item) => item.id === requestedRegionId && item.enabled);
            if (!region) throw new Error(`Unknown or disabled region "${requestedRegionId}" for image ${imageName}.`);
            if (!this.selectedRegionIds.value.includes(requestedRegionId)) {
                await this.selectRegions(
                    [...this.selectedRegionIds.value, requestedRegionId],
                    this.activeRegionId.value,
                );
            } else {
                await this.loadRegion(requestedRegionId);
            }
        }

        const matches: RegionImageContext[] = [];
        for (const [regionId, runtime] of this.regionRuntimeById) {
            if (requestedRegionId && regionId !== requestedRegionId) continue;
            if (runtime.status !== 'loaded' || !runtime.metashapeData) continue;
            const camera = runtime.metashapeData.cameras.find((item) => item.label === label);
            if (!camera) continue;
            const sensor = runtime.metashapeData.sensors.find((item) => item.id === camera.sensorId);
            if (sensor) matches.push({ regionId, data: runtime.metashapeData, camera, sensor, models: runtime.models });
        }

        if (matches.length === 1) return matches[0];
        if (matches.length > 1) {
            const regionIds = matches.map((item) => item.regionId).join(', ');
            throw new Error(`Image ${imageName} exists in multiple loaded regions (${regionIds}); add region_id to the JSON entry.`);
        }
        if (requestedRegionId) throw new Error(`Image ${imageName} was not found in region ${requestedRegionId}.`);
        throw new Error(`Image ${imageName} was not found in any loaded region; add region_id so its region can be loaded.`);
    }

    async importJSON(file: Object) {
        const lengthThreshold = 0.0001;
        const raycaster = new THREE.Raycaster();
        const root = file as Record<string, any>;
        if (!Array.isArray(root.data)) throw new Error('Import JSON must contain a data array.');
        const resolvedEntries: Array<{ entry: Record<string, any>, context: RegionImageContext }> = [];
        for (const entry of root.data) {
            if (!entry || !Array.isArray(entry.lines)) {
                throw new Error('Each import entry requires a lines array.');
            }
            if(typeof entry.image_name === 'string'){
                const lineRegionId = entry.lines[0]?.meta?.region_id;
                const requestedRegionId = typeof entry.region_id === 'string'
                    ? entry.region_id
                    : (typeof lineRegionId === 'string' ? lineRegionId : undefined);
                const context = await this.resolveRegionImageContext(entry.image_name, requestedRegionId);
                resolvedEntries.push({ entry, context });
            }else if(typeof entry.model === 'string'){
                const model = entry.model; 
                for (const line of entry.lines) {
                    const geomPoints: number[] = [];
                    let pLength = 0.0;
                    const previousPoint = new THREE.Vector3(line.points[0][0], line.points[0][1], line.points[0][2]);
                    for (const point of line.points) {
                        const linePoint = new THREE.Vector3(point[0], point[1], point[2]);
                        geomPoints.push(point[0], point[1], point[2]);
                        pLength += previousPoint.distanceTo(linePoint);
                        previousPoint.copy(linePoint)
                    }
                    if (geomPoints.length < 6 || pLength < lengthThreshold) {
                        continue;
                    }
                    const pline = new Polyline(geomPoints);
                    let severity = 1;
                    const email = useCurrentUser().value?.email;
                    if (email)
                        pline.createdBy = email;
                    if (line.meta) {
                        const { severity: lowerSeverity, Severity, ...meta } = line.meta;
                        severity = ttUtils.severityToNum(lowerSeverity ?? Severity ?? severity);
                        pline.custom = meta;
                    }
                    const metadata = pline.custom as Record<string, unknown>;
                    metadata.region_id = model.replace(/\.glb$/i, '') ;
                    pline.setSeverity(severity);
                    this.addLine(pline);
                }
            }
        }

        for (let dataI = 0; dataI < resolvedEntries.length; dataI++) {
            const { entry: data, context } = resolvedEntries[dataI];
            const { camera, sensor, models } = context;
            const cameraToWorld = camera.worldToCamera.clone().invert();
            for (const line of data.lines) {
                let points: number[];
                if (!Array.isArray(line.data) || line.data.length === 0) continue;
                if (typeof line.data[0] === "number") {
                    points = line.data;
                }
                else {
                    points = [];
                    for (const point of line.data) {
                        points.push(point[0]);
                        points.push(point[1]);
                    }
                }
                let pLength = 0.0;
                const geomPoints: number[] = [];
                const previousPoint = new THREE.Vector3();
                const rawPixel = new THREE.Vector2();
                const displaySize = ttUtils.orientedImageSize(sensor, camera.orientation);
                let hasPreviousPoint = false;
                for (let i = 0; i < points.length; i += 2) {
                    if (root.coordinate_space === 'display_pixels' || data.coordinate_space === 'display_pixels') {
                        ttUtils.imagePlanePointToPixel(
                            sensor,
                            points[i],
                            displaySize.y - points[i + 1],
                            camera.orientation,
                            rawPixel,
                        );
                    } else {
                        rawPixel.set(points[i], points[i + 1]);
                    }
                    const cameraRay = ttUtils.imagePixelToCameraRay(sensor, rawPixel.x, rawPixel.y);
                    if (!cameraRay) continue;
                    const pos = new THREE.Vector3();
                    const point = cameraRay;
                    pos.setFromMatrixPosition(cameraToWorld);
                    point.applyMatrix4(cameraToWorld);
                    const dir = new THREE.Vector3();
                    dir.subVectors(point, pos);

                    raycaster.set(pos, dir);
                    const intersects = raycaster.intersectObjects(models, true);
                    if (intersects.length === 0) {
                        console.error("Error unprojecting point: " + i / 2);
                        continue;
                    }

                    const modelPoint = intersects[0].point;
                    if (hasPreviousPoint) pLength += previousPoint.distanceTo(modelPoint);
                    geomPoints.push(modelPoint.x, modelPoint.y, modelPoint.z);
                    previousPoint.copy(modelPoint);
                    hasPreviousPoint = true;
                }
                if (geomPoints.length < 6 || pLength < lengthThreshold) {
                    console.log(`Skipping line nr: ${dataI} because it's length is: ${pLength} < ${lengthThreshold}`);
                    continue;
                }
                const pline = new Polyline(geomPoints);
                let severity = 1;
                if (line.meta) {
                    const { severity: lowerSeverity, Severity, ...meta } = line.meta;
                    severity = ttUtils.severityToNum(lowerSeverity ?? Severity ?? severity);
                    pline.custom = meta;
                }
                const metadata = pline.custom as Record<string, unknown>;
                metadata.image_name = data.image_name;
                metadata.region_id = context.regionId;
                pline.setSeverity(severity);
                this.addLine(pline);
            }
        }
    }
    
    exportJSON() {
        if (!viewer || !viewer.metashapeData) { console.log("viewer not loaded"); return; }
        type LineData = { data: number[], meta: { Severity: string } };
        type ImageEntry = { region_id: string, image_name: string, coordinate_space: string, lines: LineData[] };

        const orderedData: Record<string, ImageEntry> = {};
        const vert = new THREE.Vector3();
        for (const line of this.lines) {
            if (line.count() < 2) continue;
            const metadata = line.custom as Record<string, unknown>;
            const requestedRegionId = typeof metadata.region_id === 'string' ? metadata.region_id : null;
            const imageName = typeof metadata.image_name === 'string'
                ? metadata.image_name.replace(/\.[^.]+$/, '')
                : null;
            const candidateRegions = [...this.regionRuntimeById.entries()].filter(([regionId, runtime]) => {
                return runtime.status === 'loaded' && runtime.metashapeData
                    && (!requestedRegionId || requestedRegionId === regionId);
            });
            const matchingRegions = candidateRegions.filter(([, runtime]) => {
                return imageName && runtime.metashapeData?.cameras.some((item) => item.label === imageName);
            });
            if (!requestedRegionId && matchingRegions.length > 1) {
                console.warn(`Skipping export for ${imageName}: the image exists in multiple regions and the line has no region_id.`);
                continue;
            }
            let selectedRegion = matchingRegions[0];
            if (!selectedRegion && requestedRegionId) selectedRegion = candidateRegions[0];
            if (!selectedRegion && this.activeRegionId.value) {
                selectedRegion = candidateRegions.find(([regionId]) => regionId === this.activeRegionId.value);
            }
            if (!selectedRegion?.[1].metashapeData) continue;

            const [regionId, runtime] = selectedRegion;
            const data = runtime.metashapeData;
            let camera = imageName ? data.cameras.find((item) => item.label === imageName) : undefined;
            if (!camera) {
                line.vertexAt(0, vert);
                const cameraIdx = findClosestCamera(this, data, vert);
                if (cameraIdx < 0) continue;
                camera = data.cameras[cameraIdx];
            }

            const sensor = data.sensors.find(e => e.id === camera.sensorId);
            if (!sensor) continue;

            const entryKey = `${regionId}:${camera.label}`;
            if (!orderedData[entryKey]) {
                orderedData[entryKey] = {
                    region_id: regionId,
                    image_name: camera.label,
                    coordinate_space: 'metashape_raw_pixels',
                    lines: []
                };
            }
            let valid = true;

            const point = new THREE.Vector3();
            const imageProjectedPoints: number[] = [];
            for (let i = 0; i < line.count(); i++) {
                line.vertexAt(i, point);
                point.applyMatrix4(camera.worldToCamera);
                if (point.z < 0.0) {
                    valid = false;
                    continue;
                }
                const imagePoint = ttUtils.projectCameraPointToImagePixel(point, sensor);
                if (!imagePoint) {
                    valid = false;
                    continue;
                }
                imageProjectedPoints.push(imagePoint.x);
                imageProjectedPoints.push(imagePoint.y);
            }
            if (!valid)
                continue;

            const lineObj: LineData = {
                data: imageProjectedPoints,
                meta: {
                    Severity: ttUtils.severityNumToStr(line.severity),
                    ...line.custom,
                    region_id: regionId,
                }
            };
            orderedData[entryKey].lines.push(lineObj);
        }

        const result = {
            version: 2,
            project: this.projectName,
            coordinate_space: 'metashape_raw_pixels',
            data: Object.values(orderedData)
        };
        ttUtils.openJsonInNewTab(result);
    }
}

let viewer: Viewer | null = null;
export function createViewer(user: User, projectName: string): Viewer {
    viewer = new Viewer(user, projectName);
    return viewer;
}

export function getViewer(): Viewer | null {
    if (viewer)
        return viewer;
    console.error("Null viewer retrieved");
    return null;
}

export function destroyViewer() {
    if (viewer)
        viewer = null;
    else
        console.error("Viewer already null");
}
